import type { ApiSuccess, AuthUser, LoginResponse } from "@codestack/shared";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api/v1";
const CSRF_COOKIE = "csrf_token";
const CSRF_HEADER = "x-csrf-token";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

let accessToken: string | null = null;
let refreshPromise: Promise<boolean> | null = null;
let onUnauthorized: (() => void) | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

export function setUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler;
}

type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  skipRetry?: boolean;
};

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === "") continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.pathname + url.search;
}

async function refreshSession(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) return false;

      const payload = (await response.json()) as ApiSuccess<LoginResponse>;
      accessToken = payload.data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

async function rawRequest<T>(path: string, options: RequestOptions): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json" };

  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  if (method !== "GET") {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) headers[CSRF_HEADER] = csrf;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      credentials: "include",
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });
  } catch {
    throw new ApiError(0, "NETWORK_ERROR", "Unable to reach the API.");
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const errorBody = payload as
      | { error?: { code?: string; message?: string; details?: unknown } }
      | null;

    throw new ApiError(
      response.status,
      errorBody?.error?.code ?? "REQUEST_FAILED",
      errorBody?.error?.message ?? "The request could not be completed.",
      errorBody?.error?.details,
    );
  }

  return (payload as ApiSuccess<T>).data;
}

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  try {
    return await rawRequest<T>(path, options);
  } catch (error) {
    const isAuthFailure =
      error instanceof ApiError && (error.status === 401 || error.status === 403);

    if (!isAuthFailure || options.skipRetry) throw error;

    const refreshed = await refreshSession();
    if (!refreshed) {
      accessToken = null;
      onUnauthorized?.();
      throw error;
    }

    return rawRequest<T>(path, { ...options, skipRetry: true });
  }
}

/**
 * Downloads a binary response (CSV/PDF export) and saves it to disk.
 *
 * `apiRequest` always parses JSON, so exports need their own path. The bearer
 * token is attached the same way, and a 401 triggers one session refresh
 * before giving up — an export can easily be the first request after the
 * access token expires.
 */
export async function downloadFile(
  path: string,
  query: Record<string, string | number | boolean | undefined | null>,
  fallbackName: string,
): Promise<void> {
  const attempt = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    return fetch(buildUrl(path, query), { headers, credentials: "include" });
  };

  let response = await attempt();

  if (response.status === 401 || response.status === 403) {
    const refreshed = await refreshSession();
    if (refreshed) {
      response = await attempt();
    } else {
      accessToken = null;
      onUnauthorized?.();
    }
  }

  if (!response.ok) {
    // Errors are JSON even on the export route.
    let message = "The export could not be generated.";
    let code = "EXPORT_FAILED";
    try {
      const body = (await response.json()) as { error?: { code?: string; message?: string } };
      message = body.error?.message ?? message;
      code = body.error?.code ?? code;
    } catch {
      /* non-JSON error body — keep the defaults */
    }
    throw new ApiError(response.status, code, message);
  }

  // Prefer the server's filename so the platform slug and date range survive.
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = match?.[1] ?? fallbackName;

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke on the next tick so Safari has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const authApi = {
  login: async (email: string, password: string): Promise<LoginResponse> => {
    const result = await apiRequest<LoginResponse>("/auth/login", {
      method: "POST",
      body: { email, password },
      skipRetry: true,
    });
    accessToken = result.accessToken;
    return result;
  },
  me: () => apiRequest<AuthUser>("/auth/me"),
  logout: async () => {
    try {
      await apiRequest<void>("/auth/logout", { method: "POST", skipRetry: true });
    } finally {
      accessToken = null;
    }
  },
  changePassword: (currentPassword: string, newPassword: string) =>
    apiRequest<void>("/auth/change-password", {
      method: "POST",
      body: { currentPassword, newPassword },
    }),
  bootstrap: () => refreshSession(),
};
